import React, { useState } from 'react';
import { getDatabase, ref, push, set } from 'firebase/database';
import './Registration.css';
import { FaSpinner } from 'react-icons/fa';
import ImageKitUpload from './ImageKitUpload';
import './ImageKitUpload.css';
import { nigerianStates } from '../data/locationData';
import { sessions, classes, studentStatus as studentStatusOptions } from '../data/schoolData';

const StudentRegistration = () => {
    const [formData, setFormData] = useState({
        fullName: '',
        email: '',
        password: '',
        dateOfBirth: '',
        className: '',
        address: '',
        guardianName: '',
        guardianPhone: '',
        guardianAddress: '',
        session: '',
        studentStatus: '',
        state: '',
        lga: '',
    });
    const [profilePicUrl, setProfilePicUrl] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState(false);

    const handleInputChange = (e) => {
        const { id, value } = e.target;
        if (id === 'state') {
            setFormData(prev => ({ ...prev, state: value, lga: '' }));
        } else {
            setFormData(prev => ({ ...prev, [id]: value }));
        }
    };

    const onUploadSuccess = (res) => {
        setProfilePicUrl(res.url);
    };

    const onUploadError = (err) => {
        setError('Failed to upload profile picture. Please try again.');
        console.error('Upload error:', err);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setSuccess(false);

        if (!profilePicUrl) {
            setError('Please upload a student passport or profile picture.');
            return;
        }
        if (formData.password.length < 6) {
            setError('Password must be at least 6 characters long.');
            return;
        }

        setLoading(true);

        try {
            const db = getDatabase();

            const pendingUserData = {
                ...formData,
                role: 'student',
                profilePicUrl,
                status: 'pending',
                submittedAt: new Date().toISOString(),
            };

            const newUserRef = push(ref(db, 'pending_users'));
            await set(newUserRef, pendingUserData);

            setSuccess(true);

        } catch (err) {
            setError(`Failed to submit registration: ${err.message}`);
        } finally {
            setLoading(false);
        }
    };

    if (success) {
        return (
            <div className="registration-container">
                <h2>Student Registration Submitted!</h2>
                <p className="form-text" style={{ color: '#28a745', fontSize: '1.1rem' }}>
                    The student's application has been sent for administrative review.
                </p>
            </div>
        );
    }

    return (
        <div className="registration-container">
            <h2>New Student Registration</h2>
            {error && <p className="alert alert-danger">{error}</p>}
            <form onSubmit={handleSubmit}>
                <div className="profile-pic-upload-group">
                    <label>Upload Student Passport</label>
                    <ImageKitUpload
                        onUploadSuccess={onUploadSuccess}
                        onUploadError={onUploadError}
                    />
                </div>

                <div className="form-group">
                    <label htmlFor="fullName">Full Name</label>
                    <input id="fullName" type="text" className="form-control" value={formData.fullName} onChange={handleInputChange} required />
                </div>

                <div className="form-group">
                    <label htmlFor="email">Email Address</label>
                    <input id="email" type="email" className="form-control" value={formData.email} onChange={handleInputChange} required />
                </div>

                <div className="form-group">
                    <label htmlFor="password">Password</label>
                    <input id="password" type="password" className="form-control" value={formData.password} onChange={handleInputChange} required />
                </div>

                <div className="form-group">
                    <label htmlFor="dateOfBirth">Date of Birth</label>
                    <input id="dateOfBirth" type="date" className="form-control" value={formData.dateOfBirth} onChange={handleInputChange} required />
                </div>

                <div className="form-group">
                    <label htmlFor="session">Session</label>
                    <select id="session" className="form-select" value={formData.session} onChange={handleInputChange} required>
                        <option value="">Select Session</option>
                        {sessions.map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                </div>

                <div className="form-group">
                    <label htmlFor="className">Assign to Class</label>
                    <select id="className" className="form-select" value={formData.className} onChange={handleInputChange} required>
                        <option value="">Select a class</option>
                        {classes.map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
                </div>

                <div className="form-group">
                    <label htmlFor="studentStatus">Status</label>
                    <select id="studentStatus" className="form-select" value={formData.studentStatus} onChange={handleInputChange} required>
                        <option value="">Select Status</option>
                        {studentStatusOptions.map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                </div>

                <div className="form-group">
                    <label htmlFor="state">State of Origin</label>
                    <select id="state" className="form-select" value={formData.state} onChange={handleInputChange} required>
                        <option value="">Select State</option>
                        {nigerianStates.map(s => <option key={s.name} value={s.name}>{s.name}</option>)}
                    </select>
                </div>

                <div className="form-group">
                    <label htmlFor="lga">LGA of Origin</label>
                    <select id="lga" className="form-select" value={formData.lga} onChange={handleInputChange} required>
                        <option value="">Select LGA</option>
                        {formData.state && nigerianStates.find(s => s.name === formData.state)?.lgas.map(l => <option key={l} value={l}>{l}</option>)}
                    </select>
                </div>

                <div className="form-group">
                    <label htmlFor="address">Student's Home Address</label>
                    <input id="address" type="text" className="form-control" value={formData.address} onChange={handleInputChange} required />
                </div>

                <hr style={{margin: '2rem 0'}} />

                <h4>Guardian Information</h4>

                <div className="form-group">
                    <label htmlFor="guardianName">Guardian's Full Name</label>
                    <input id="guardianName" type="text" className="form-control" value={formData.guardianName} onChange={handleInputChange} required />
                </div>

                <div className="form-group">
                    <label htmlFor="guardianPhone">Guardian's Phone Number</label>
                    <input id="guardianPhone" type="tel" className="form-control" value={formData.guardianPhone} onChange={handleInputChange} required />
                </div>
                
                <div className="form-group">
                    <label htmlFor="guardianAddress">Guardian's Address</label>
                    <input id="guardianAddress" type="text" className="form-control" value={formData.guardianAddress} onChange={handleInputChange} required />
                </div>

                <button type="submit" className="btn-submit" disabled={loading}>
                    {loading ? <><FaSpinner className="spinner"/> Submitting for Approval...</> : 'Submit Student Registration'}
                </button>
            </form>
        </div>
    );
};

export default StudentRegistration;
